import React from "react";
import {
  ActivityIndicator,
  Image,
  ImageBackground,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { knexo_logo, sign_bg } from "../../../constants/icons";
import { SignUpViewProps } from "../auth.types";
import PasswordInput from "./PasswordInput";

export default function SignUpView({
  name,
  email,
  password,
  confirmPassword,
  isLoading,
  isSaving,
  error,
  canSignUp,
  onNameChange,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onPressJoin,
  onSwitchToSignIn,
}: SignUpViewProps) {
  if (isLoading) {
    return (
      <View style={styles.root} testID="join-loading">
        <ImageBackground source={sign_bg} style={styles.bg} resizeMode="cover">
          <View style={styles.topBar}>
            <Image
              source={knexo_logo}
              style={styles.brandLogo}
              resizeMode="contain"
            />
          </View>

          <View style={styles.centerWrap}>
            <View style={styles.card}>
              <ActivityIndicator />
              <Text style={styles.loadingText}>Loading...</Text>
            </View>
          </View>
        </ImageBackground>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.root}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      testID="join-screen"
    >
      <ImageBackground source={sign_bg} style={styles.bg} resizeMode="cover">
        {/* Top-left logo */}
        <View style={styles.topBar}>
          <Image
            source={knexo_logo}
            style={styles.brandLogo}
            resizeMode="contain"
          />
        </View>

        {/* Center card */}
        <View style={styles.centerWrap}>
          <View style={styles.card}>
            <View style={styles.header}>
              <Text style={styles.title}>Create your KNEXO account</Text>
              <Text style={styles.subtitle}>
                Join guided, respectful, time-limited conversations designed to
                feel comfortable.
              </Text>
            </View>

            {/* NAME */}
            <TextInput
              style={styles.input}
              placeholder="Name"
              placeholderTextColor="#8B9099"
              value={name}
              onChangeText={onNameChange}
              autoCapitalize="words"
              returnKeyType="next"
              testID="name-input"
            />

            {/* EMAIL */}
            <TextInput
              style={styles.input}
              placeholder="Email"
              placeholderTextColor="#8B9099"
              value={email}
              onChangeText={onEmailChange}
              autoCapitalize="none"
              keyboardType="email-address"
              returnKeyType="next"
              testID="email-input"
            />

            {/* PASSWORD */}
            <PasswordInput
              style={[styles.input, styles.inputMutedBorder]}
              placeholder="Password"
              placeholderTextColor="#8B9099"
              value={password}
              onChangeText={onPasswordChange}
              returnKeyType="next"
              testID="password-input"
            />

            {/* CONFIRM PASSWORD */}
            <PasswordInput
              style={[styles.input, styles.inputMutedBorder]}
              placeholder="Confirm password"
              placeholderTextColor="#8B9099"
              value={confirmPassword}
              onChangeText={onConfirmPasswordChange}
              returnKeyType="done"
              onSubmitEditing={onPressJoin}
              testID="confirm-password-input"
            />

            {error ? (
              <Text style={styles.errorText} testID="signup-error">
                {error}
              </Text>
            ) : null}

            <TouchableOpacity
              onPress={onPressJoin}
              disabled={!canSignUp}
              style={[styles.btn, !canSignUp && styles.btnDisabled]}
              testID="join-button"
            >
              {isSaving ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.btnText}>Sign Up</Text>
              )}
            </TouchableOpacity>

            {onSwitchToSignIn && (
              <TouchableOpacity
                onPress={onSwitchToSignIn}
                style={styles.switchButton}
                testID="switch-to-signin-button"
              >
                <Text style={styles.switchText}>
                  Already have an account?{" "}
                  <Text style={styles.switchTextBold}>Sign In</Text>
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </ImageBackground>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "#fff",
  },

  bg: {
    flex: 1,
    width: "100%",
    height: "100%",
  },

  topBar: {
    paddingTop: 24,
    paddingHorizontal: 24,
  },

  brandLogo: {
    width: 119,
    height: 148,
  },

  centerWrap: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingBottom: 24,
  },

  card: {
    width: "100%",
    maxWidth: 587,
    minHeight: 601,
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#D8DBE1",
    paddingHorizontal: 48,
    paddingVertical: 48,

    shadowColor: "#000",
    shadowOpacity: 0.05,
    shadowRadius: 8,
    shadowOffset: { width: 8, height: 8 },
    elevation: 4,
    marginBottom: 110,
  },

  header: {
    width: "100%",
    marginBottom: 18,
  },

  title: {
    fontFamily: "LexendBold",
    fontSize: 32,
    lineHeight: 38,
    fontWeight: "700",
    color: "#2B2B2F",
  },

  subtitle: {
    marginTop: 10,
    fontFamily: "Lexend",
    fontSize: 16,
    lineHeight: 22,
    fontWeight: "400",
    color: "#2B2B2F",
  },

  input: {
    width: "100%",
    height: 56,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#7EADEB",
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 18,
    fontSize: 16,
    color: "#2B2B2F",
    marginTop: 14,
    fontFamily: "Lexend",
  },

  inputMutedBorder: {
    borderColor: "#D8DBE1",
  },

  btn: {
    width: "100%",
    height: 56,
    borderRadius: 16,
    backgroundColor: "#3A6FB1",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 16,
  },
  btnDisabled: { opacity: 0.55 },
  btnText: {
    fontFamily: "Lexend",
    color: "#FFFFFF",
    fontWeight: "600",
    fontSize: 16,
  },

  loadingText: {
    marginTop: 12,
    textAlign: "center",
    color: "#2B2B2F",
    fontFamily: "Lexend",
    fontSize: 14,
  },

  switchButton: {
    marginTop: 18,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 8,
  },
  switchText: {
    fontFamily: "Lexend",
    fontSize: 12,
    color: "#2B2B2F",
    textAlign: "center",
  },
  switchTextBold: {
    fontFamily: "Lexend",
    fontWeight: "600",
    color: "#3A6FB1",
  },

  errorText: {
    marginTop: 10,
    color: "#D32F2F",
    fontSize: 13,
    fontFamily: "Lexend",
  },
});
